import { css } from '@emotion/react';
import { Clear } from '@mui/icons-material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { IconButton, Stack, Typography } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { IItemWithId, SortableList } from '../../../../../components/SortableList/SortableList';
import { BasicDragHandle, SortableItem } from '../../../../../components/SortableList/SortableItem';
import { kpiConfigByIdMapAtom } from '../../../../../services/state/KPIConfigState';
import {
  CustomKpiTemplateFormValues,
  CustomTemplateSectionType,
} from '../../../../../schemas/kpi-template.schema';
import {
  selectedKpiConfigIdAtom,
  showKpiConfigFormAtom,
} from '../../../kpi-config-form/kpi-config-form-state';
import { useQuantitativeCustomKpis } from '../hooks/useFilteredKpiConfigs';
import { KpiConfigSelector } from '../KpiConfigSelector';
import { kpiItemBaseCss, kpiListCss, kpiRowAddIn } from './section-editor.styles';

const kpiRowCss = css({
  ...kpiItemBaseCss,
  gridTemplateColumns: 'auto 1fr auto',
  padding: '0 0.5rem',
});

const kpiRowAnimatedCss = css({
  animation: `${kpiRowAddIn} 200ms ease-out`,
});

interface SortableKpiItem extends IItemWithId {
  id: number;
  displayName: string;
}

interface QuantitativeSectionEditorProps {
  sectionIndex: number;
  onCreateNewKpi?: (sectionIndex: number, sectionType: CustomTemplateSectionType, kpiName?: string) => void;
}

export function useQuantitativeSectionEditor(sectionIndex: number) {
  const { setValue, watch } = useFormContext<CustomKpiTemplateFormValues>();
  const kpiConfigMap = useAtomValue(kpiConfigByIdMapAtom);
  const setSelectedKpiConfigId = useSetAtom(selectedKpiConfigIdAtom);
  const setShowKpiConfigForm = useSetAtom(showKpiConfigFormAtom);
  const availableKpis = useQuantitativeCustomKpis();
  const kpiIds: number[] = watch(`sections.${sectionIndex}.kpis`);

  const items = useMemo<SortableKpiItem[]>(
    () =>
      kpiIds.map((id) => ({
        id,
        displayName: kpiConfigMap.get(id)?.displayName ?? `KPI #${id}`,
      })),
    [kpiIds, kpiConfigMap]
  );

  const handleAdd = useCallback(
    (id: number) => {
      setValue(`sections.${sectionIndex}.kpis`, [...kpiIds, id], { shouldDirty: true });
    },
    [kpiIds, setValue, sectionIndex]
  );

  const handleEdit = useCallback(
    (id: number) => {
      setSelectedKpiConfigId(id);
      setShowKpiConfigForm(true);
    },
    [setSelectedKpiConfigId, setShowKpiConfigForm]
  );

  const handleRemove = useCallback(
    (id: number) => {
      setValue(
        `sections.${sectionIndex}.kpis`,
        kpiIds.filter((kpiId) => kpiId !== id),
        { shouldDirty: true }
      );
    },
    [kpiIds, setValue, sectionIndex]
  );

  const handleReorder = useCallback(
    (reordered: SortableKpiItem[]) => {
      setValue(
        `sections.${sectionIndex}.kpis`,
        reordered.map((item) => item.id),
        { shouldDirty: true }
      );
    },
    [setValue, sectionIndex]
  );

  return { items, availableKpis, kpiIds, handleAdd, handleEdit, handleRemove, handleReorder };
}

export function QuantitativeSectionEditor({ sectionIndex, onCreateNewKpi }: QuantitativeSectionEditorProps) {
  const { items, availableKpis, kpiIds, handleAdd, handleEdit, handleRemove, handleReorder } =
    useQuantitativeSectionEditor(sectionIndex);
  const initialIdsRef = useRef<Set<number>>(new Set(kpiIds));

  return (
    <Stack>
      {items.length > 0 && (
        <div css={kpiListCss}>
          <SortableList
            items={items}
            onChange={handleReorder}
            renderItem={(item) => (
              <KpiRow
                item={item}
                onEdit={handleEdit}
                onRemove={handleRemove}
                isNew={!initialIdsRef.current.has(item.id)}
              />
            )}
          />
        </div>
      )}
      <Stack sx={{ p: '0.5rem' }}>
        <KpiConfigSelector
          availableKpis={availableKpis}
          selectedKpiIds={kpiIds}
          onSelect={handleAdd}
          onCreateNew={
            onCreateNewKpi
              ? (kpiName) => onCreateNewKpi(sectionIndex, CustomTemplateSectionType.quantitative, kpiName)
              : undefined
          }
        />
      </Stack>
    </Stack>
  );
}

interface KpiRowProps {
  item: SortableKpiItem;
  onEdit: (id: number) => void;
  onRemove: (id: number) => void;
  isNew?: boolean;
}

function KpiRow({ item, onEdit, onRemove, isNew }: KpiRowProps) {
  return (
    <SortableItem id={item.id}>
      <div css={[kpiRowCss, isNew && kpiRowAnimatedCss]}>
        <BasicDragHandle />
        <Typography variant='body2'>{item.displayName}</Typography>
        <span>
          <IconButton size='small' onClick={() => onEdit(item.id)} aria-label={`Edit ${item.displayName}`}>
            <EditOutlinedIcon fontSize='small' />
          </IconButton>
          <IconButton
            size='small'
            onClick={() => onRemove(item.id)}
            aria-label={`Remove ${item.displayName}`}
          >
            <Clear fontSize='small' />
          </IconButton>
        </span>
      </div>
    </SortableItem>
  );
}
